import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Search, Edit2, ArrowUpDown, ArrowUp, ArrowDown, List, Layers, GripVertical } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { CategoryData, VideoData, getUserCategories, deleteCategory, getUserVideos, updateCategory } from '@/lib/firebase';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import AddCategoryModal from '../components/add-category-modal';
import EditCategoryModal from '../components/edit-category-modal';
import { logger } from '@/lib/logger';

export default function CategoriesPage() {
  const [categories, setCategories] = useState<CategoryData[]>([]);

  const [videos, setVideos] = useState<VideoData[]>([]);
  const [moduleFilter, setModuleFilter] = useState('');
  const [libraryFilter, setLibraryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'name' | 'videoCount'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'list' | 'grouped'>('list');
  const [draggedCategory, setDraggedCategory] = useState<CategoryData | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const loadData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const [categoriesData, videosData] = await Promise.all([
        getUserCategories(),
        getUserVideos(user.uid)
      ]);
      setCategories(categoriesData);
      setVideos(videosData);
    } catch (error) {
      logger.error('Failed to load data:', error);
      toast({
        title: "Failed to load categories",
        description: "Please try refreshing the page.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  const handleEditCategory = (category: CategoryData) => {
    setEditingCategory(category);
    setEditModalOpen(true);
  };

  const handleDeleteCategory = async (categoryId: string, categoryName: string) => {
    if (!confirm(`Are you sure you want to delete the "${categoryName}" category? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await deleteCategory(categoryId);
      loadData();
      
      toast({
        title: "Category deleted",
        description: `"${categoryName}" has been removed from your categories.`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to delete category",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getVideoCount = (categoryId: string, categoryTag: string) => {
    // Count videos that have this category in their contentCategories array, excluding blocked videos
    return videos.filter(video => 
      video.contentCategories && 
      video.status !== 'Blocked' &&
      (video.contentCategories.includes(categoryId) || video.contentCategories.includes(categoryTag))
    ).length;
  };

  const getModuleColor = (module: string) => {
    switch (module) {
      case 'body': return '#d059c4';
      case 'mind': return '#66cee7';
      case 'general': return '#595ebe';
      default: return '#6b7280';
    }
  };

  const handleSort = (field: 'name' | 'videoCount') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: 'name' | 'videoCount') => {
    if (sortBy !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };

  const filteredAndSortedCategories = React.useMemo(() => {
    let filtered = categories.filter(category => {
      const matchesSearch = category.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           category.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesModule = !moduleFilter || category.module === moduleFilter;
      const matchesLibrary = !libraryFilter || category.contentLibrary === libraryFilter;
      
      return matchesSearch && matchesModule && matchesLibrary;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'name') {
        const nameA = (a.title || '').toLowerCase();
        const nameB = (b.title || '').toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (sortBy === 'videoCount') {
        const countA = getVideoCount(a.id!, a.tag);
        const countB = getVideoCount(b.id!, b.tag);
        comparison = countA - countB;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [categories, searchQuery, moduleFilter, libraryFilter, sortBy, sortOrder, videos]);

  // Get unique libraries from categories, optionally filtered by module
  const availableLibraries = React.useMemo(() => {
    const librariesFromCategories = categories
      .filter(category => !moduleFilter || category.module === moduleFilter)
      .map(category => category.contentLibrary)
      .filter(Boolean);
    
    return Array.from(new Set(librariesFromCategories)).sort();
  }, [categories, moduleFilter]);

  // Group categories by module and library for the grouped view
  const groupedCategories = React.useMemo(() => {
    const groups: Record<string, Record<string, CategoryData[]>> = {};
    
    categories.forEach(category => {
      if (!groups[category.module]) {
        groups[category.module] = {};
      }
      if (!groups[category.module][category.contentLibrary]) {
        groups[category.module][category.contentLibrary] = [];
      }
      groups[category.module][category.contentLibrary].push(category);
    });
    
    // Sort categories within each group by displayOrder
    Object.keys(groups).forEach(module => {
      Object.keys(groups[module]).forEach(library => {
        groups[module][library].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      });
    });
    
    return groups;
  }, [categories]);

  const handleDragStart = (category: CategoryData) => {
    setDraggedCategory(category);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (targetCategory: CategoryData, module: string, library: string) => {
    if (!draggedCategory || draggedCategory.id === targetCategory.id) {
      setDraggedCategory(null);
      return;
    }

    // Only allow reordering within the same module/library
    if (draggedCategory.module !== module || draggedCategory.contentLibrary !== library) {
      toast({
        title: "Cannot reorder",
        description: "Categories can only be reordered within the same module and library.",
        variant: "destructive",
      });
      setDraggedCategory(null);
      return;
    }

    try {
      const categoriesInGroup = groupedCategories[module][library];
      const draggedIndex = categoriesInGroup.findIndex(c => c.id === draggedCategory.id);
      const targetIndex = categoriesInGroup.findIndex(c => c.id === targetCategory.id);

      if (draggedIndex === -1 || targetIndex === -1) return;

      // Reorder categories
      const newOrder = [...categoriesInGroup];
      const [removed] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, removed);

      // Create a map of category IDs to new displayOrder values
      const displayOrderMap = new Map<string, number>();
      newOrder.forEach((cat, index) => {
        if (cat.id) {
          displayOrderMap.set(cat.id, index);
        }
      });

      // Optimistically update local state
      setCategories(prevCategories => 
        prevCategories.map(cat => {
          const newDisplayOrder = displayOrderMap.get(cat.id!);
          if (newDisplayOrder !== undefined) {
            return { ...cat, displayOrder: newDisplayOrder };
          }
          return cat;
        })
      );

      // Persist changes to Firestore in background
      const updatePromises = newOrder.map((cat, index) => 
        updateCategory(cat.id!, { displayOrder: index })
      );

      await Promise.all(updatePromises);
      
      toast({
        title: "Order updated",
        description: "Category order has been successfully updated.",
      });
    } catch (error: any) {
      // Revert on error by reloading data
      await loadData();
      
      toast({
        title: "Failed to update order",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDraggedCategory(null);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="h-4 bg-slate-200 rounded w-1/2"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Categories</h1>
            <p className="text-slate-600">Manage your content categories and organization</p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                data-testid="button-list-view"
              >
                <List className="w-4 h-4 mr-1" />
                List
              </Button>
              <Button
                variant={viewMode === 'grouped' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grouped')}
                data-testid="button-grouped-view"
              >
                <Layers className="w-4 h-4 mr-1" />
                Module/Library
              </Button>
            </div>
            <Button
              onClick={() => setAddModalOpen(true)}
              className="bg-primary text-white hover:bg-primary/90 flex items-center space-x-2"
              data-testid="button-add-category"
            >
              <Plus size={16} />
              <span>Add Category</span>
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    type="text"
                    placeholder="Search categories..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Select value={moduleFilter || "all"} onValueChange={(value) => {
                  setModuleFilter(value === "all" ? "" : value);
                  setLibraryFilter(""); // Reset library filter when module changes
                }}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All Modules" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Modules</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="mind">Mind</SelectItem>
                    <SelectItem value="body">Body</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={libraryFilter || "all"} onValueChange={(value) => setLibraryFilter(value === "all" ? "" : value)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="All Libraries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Libraries</SelectItem>
                    {availableLibraries.map(library => (
                      <SelectItem key={library} value={library}>
                        {library}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sort Controls - Only show for list view */}
        {viewMode === 'list' && (
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700">Sort by:</span>
                <Button
                  variant={sortBy === 'name' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2"
                >
                  Category Name
                  {getSortIcon('name')}
                </Button>
                <Button
                  variant={sortBy === 'videoCount' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleSort('videoCount')}
                  className="flex items-center gap-2"
                >
                  Video Count
                  {getSortIcon('videoCount')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* List View */}
        {viewMode === 'list' && (
        <div className="grid gap-4">
          {filteredAndSortedCategories.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-slate-600 mb-4">
                  {categories.length === 0 
                    ? "No categories created yet." 
                    : "No categories match your current filters."
                  }
                </p>
                <Button
                  onClick={() => setAddModalOpen(true)}
                  variant="outline"
                  className="flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>Create your first category</span>
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredAndSortedCategories.map((category) => (
              <Card key={category.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: getModuleColor(category.module) }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-1">
                          <h3 className="font-semibold text-slate-900 text-sm">
                            {category.title}
                          </h3>
                          <Badge variant="outline" className="text-xs px-2 py-0">
                            {category.tag}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            {getVideoCount(category.id!, category.tag)} videos
                          </span>
                        </div>
                        <p className="text-xs text-slate-600">
                          {category.contentLibrary} • {category.module}
                        </p>
                        {category.description && (
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                            {category.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditCategory(category)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCategory(category.id!, category.title)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
        )}

        {/* Grouped View by Module/Library */}
        {viewMode === 'grouped' && (
          <div className="space-y-6">
            {categories.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-slate-600 mb-4">No categories created yet.</p>
                  <Button
                    onClick={() => setAddModalOpen(true)}
                    variant="outline"
                    className="flex items-center space-x-2"
                  >
                    <Plus size={16} />
                    <span>Create your first category</span>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              Object.keys(groupedCategories).sort().map(module => (
                <div key={module} className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <span 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: getModuleColor(module) }}
                    />
                    <h2 className="text-xl font-bold text-slate-900 capitalize">{module}</h2>
                  </div>
                  
                  {Object.keys(groupedCategories[module]).sort().map(library => (
                    <Card key={`${module}-${library}`}>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base font-semibold text-slate-700">{library}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {groupedCategories[module][library].map((category, index) => (
                            <div
                              key={category.id}
                              draggable
                              onDragStart={() => handleDragStart(category)}
                              onDragOver={handleDragOver}
                              onDrop={() => handleDrop(category, module, library)}
                              className={`flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 cursor-move transition-all ${
                                draggedCategory?.id === category.id ? 'opacity-50' : ''
                              }`}
                              data-testid={`category-item-${category.id}`}
                            >
                              <div className="flex items-center space-x-3 flex-1">
                                <GripVertical className="w-4 h-4 text-slate-400" />
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-slate-600 w-6">#{index + 1}</span>
                                  <h3 className="font-semibold text-slate-900 text-sm">
                                    {category.title}
                                  </h3>
                                  <Badge variant="outline" className="text-xs px-2 py-0">
                                    {category.tag}
                                  </Badge>
                                  <span className="text-xs text-slate-400">
                                    {getVideoCount(category.id!, category.tag)} videos
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditCategory(category)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  data-testid={`button-edit-${category.id}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteCategory(category.id!, category.title)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`button-delete-${category.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ))
            )}
          </div>
        )}

        {/* Add Category Modal */}
        <AddCategoryModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onCategoryAdded={loadData}
        />

        {/* Edit Category Modal */}
        <EditCategoryModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingCategory(null);
          }}
          category={editingCategory}
          onCategoryUpdated={loadData}
        />
      </div>
    </div>
  );
}